import { action, computed, observable, makeObservable } from 'mobx';
import {ValidatorFn} from '../misc/validators';

function toValidatorFunctions<TEntity>(argument: ValidatorFn<TEntity> | ValidatorFn<TEntity> [] | null | undefined):
  ValidatorFn<TEntity> [] {
  if (argument === null || argument === undefined)
    return [];
  if (Array.isArray(argument))
    return argument;
  if (typeof argument === 'function')
    return [argument];
  return [];
}

type ValueChangeListener<TEntity> = (value: TEntity, name: string) => void;

function toValueChangeListeners<TEntity>(argument: ValueChangeListener<TEntity> | ValueChangeListener<TEntity> [] | null | undefined):
  ValueChangeListener<TEntity>[] {
  if (argument === null || argument === undefined)
    return [];
  if (Array.isArray(argument))
    return argument;
  if (typeof argument === 'function')
    return [argument];
  return [];
}

export class Control<TEntity> {
  private readonly validators: ValidatorFn<TEntity>[];

  @observable
  private _touched: boolean = false;

  @observable.ref
  private _value: TEntity;

  private _valueChangeListeners: ValueChangeListener<TEntity>[];

  constructor(readonly name: string,
              initialValue: TEntity,
              validators?: ValidatorFn<TEntity> | ValidatorFn<TEntity> [] | null,
              valueChanges?: ValueChangeListener<TEntity> | ValueChangeListener<TEntity>[]) {
    makeObservable(this);
    this._value = initialValue;
    this.validators = toValidatorFunctions(validators);
    this._valueChangeListeners = toValueChangeListeners(valueChanges);
  }

  @computed
  public get value(): TEntity {
    return this._value;
  }

  @action
  public setValue = (value: TEntity) => {
    if (this._value !== value) {
      this._value = value;
      for (let listener of this._valueChangeListeners) {
        listener(value, this.name);
      }
    }
  };

  @computed
  get invalid(): boolean {
    const value = this._value;

    for (let validator of this.validators)
      if (validator(value))
        return true;

    return false;
  }

  @computed
  get valid(): boolean {
    return !this.invalid;
  }

  @computed
  get formGroupClassName(): string {
    if (this.touched && this.invalid)
      return 'form-group has-error';
    return 'form-group';
  }

  @computed
  get untouched(): boolean {
    return !this._touched;
  }

  @computed
  get touched(): boolean {
    return this._touched;
  }

  @computed
  get invalidAndTouched(): boolean {
    return this.touched && this.invalid;
  }

  @action
  markTouched = () => {
    this._touched = true;
  };

  @action
  markUntouched = () => {
    this._touched = false;
  };

  addValueChangeListener = (listener: ValueChangeListener<TEntity>) => {
    this._valueChangeListeners.push(listener);
  };

  setValueChangeListeners = (listeners?: ValueChangeListener<TEntity> | ValueChangeListener<TEntity>[]) => {
    this._valueChangeListeners = toValueChangeListeners(listeners);
  };
}


